import { intl } from "LocaleWrapper";
import { FormGroup } from "@cofano/cck";
import { Box, Grid2 as Grid, Typography, useTheme } from "@mui/material";
import { FormArrayContainer, FormArrayRow } from "components/cck/FormArray";
import { DataFormat } from "utils";
import { ProductLink } from "utils/LinkUtils";
import { TankConfiguration, TankPosition } from "__generated__/enums";
import { TankFragment } from "__generated__/view/ships/graphql/ship.fragments.generated";
import { VoyageTankListCargoFragment } from "__generated__/view/voyages/graphql/voyage-info.fragments.generated";

const filterTanksByPosition = (tanks: TankFragment[], position: TankPosition) => tanks.filter(t => t.position === position);

interface ShipTanksProps {
	tanks: TankFragment[];
	tankConfiguration: TankConfiguration | null;
	cargos?: Record<number, {
		cargo: VoyageTankListCargoFragment;
		index: number;
	}[]>;
	selectedCargo?: { id?: number; };
	showCargos: boolean;
}

const ShipTanksDetails = ({ tanks, tankConfiguration, cargos, selectedCargo, showCargos }: ShipTanksProps) => {
	const theme = useTheme();

	if (tanks.length === 0) {
		return (
			<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
				<Typography
					component="span"
					color="grey"
					fontSize={14}
					fontStyle="italic">{intl.formatMessage({ id: "SHIP_TANKS_NOT_LINKED" })}
				</Typography>
			</Box>
		);
	}

	return (
		<Grid container sx={{ backgroundColor: theme.palette.mode === "dark" ? "background.paper" : "rgb(240, 240, 240)" }}>
			{tankConfiguration === TankConfiguration.CenterTanks ? (
				<ShipCenterTanks tanks={tanks}
					cargos={cargos}
					selectedCargo={selectedCargo}
					showCargos={showCargos} />
			) : tankConfiguration === TankConfiguration.PortStarboardTanks ? (
				<>
					<Grid size={{ xs: 6 }}>
						<ShipPortTanks tanks={tanks}
							cargos={cargos}
							selectedCargo={selectedCargo}
							showCargos={showCargos} />
					</Grid>

					<Grid size={{ xs: 6 }}>
						<ShipStarboardTanks tanks={tanks}
							cargos={cargos}
							selectedCargo={selectedCargo}
							showCargos={showCargos} />
					</Grid>
				</>
			) : (
				<></>
			)}
		</Grid>
	);
};

const ShipCenterTanks = ({
	tanks,
	cargos,
	selectedCargo,
	showCargos,
}: Pick<ShipTanksProps, "tanks" | "cargos" | "selectedCargo" | "showCargos">) => {
	return (
		<ShipTanksGeneral
			tanks={tanks}
			position={TankPosition.Center}
			configuration={TankConfiguration.CenterTanks}
			cargos={cargos}
			selectedCargo={selectedCargo}
			showCargos={showCargos} />
	);
};

const ShipStarboardTanks = ({
	tanks,
	cargos,
	selectedCargo,
	showCargos,
}: Pick<ShipTanksProps, "tanks" | "cargos" | "selectedCargo" | "showCargos">) => {
	return (
		<ShipTanksGeneral
			tanks={tanks}
			position={TankPosition.Starboard}
			configuration={TankConfiguration.PortStarboardTanks}
			cargos={cargos}
			selectedCargo={selectedCargo}
			showCargos={showCargos} />
	);
};

const ShipPortTanks = ({
	tanks,
	cargos,
	selectedCargo,
	showCargos,
}: Pick<ShipTanksProps, "tanks" | "cargos" | "selectedCargo" | "showCargos">) => {
	return (
		<ShipTanksGeneral
			tanks={tanks}
			position={TankPosition.Port}
			configuration={TankConfiguration.PortStarboardTanks}
			cargos={cargos}
			selectedCargo={selectedCargo}
			showCargos={showCargos} />
	);
};

const ShipTanksGeneral = ({
	tanks,
	position,
	configuration,
	cargos,
	selectedCargo,
	showCargos,
}: Pick<ShipTanksProps, "tanks" | "cargos" | "selectedCargo" | "showCargos"> & {
	position: TankPosition;
	configuration: TankConfiguration;
}) => {
	const filteredTanks = filterTanksByPosition(tanks, position);

	const theme = useTheme();
	const selectedTankColor = theme.palette.primary.main;
	const cargoTanKColor = theme.palette.grey[500];

	return (
		<Grid size={{ xs: 12 }}>
			<FormArrayContainer>
				<Grid container spacing={2} flexDirection="column">
					{filteredTanks.map((tank) => {
						const cargosInTank = (cargos?.[tank.id] ?? []).filter(
							(item, index, array) =>
								array.findIndex(
									other => other.cargo.id === item.cargo.id,
								) === index,
						);
						const hasSelectedCargoInTank = selectedCargo?.id && cargosInTank.some(c => c.cargo.id === selectedCargo.id);
						const color = hasSelectedCargoInTank ? selectedTankColor : cargosInTank.length > 0 ? cargoTanKColor : "transparent";
						const hasAccent = hasSelectedCargoInTank || cargosInTank.length > 0;

						return (
							<Grid key={tank.id} size={12}>
								<FormArrayRow
									color={color}
									title={null}
									collapsable={false}
									slotProps={{
										root: {
											variant: "elevation",
											square: false,
											sx: {
												boxShadow: (theme) =>
													hasAccent
														? `inset 4px 0 0 ${color}, ${theme.shadows[1]}`
														: theme.shadows[1],
											},
										},
										content: {
											sx: {
												borderTop: "none",
												padding: "12px 16px !important",
											},
										},
									}}
								>
									<Grid container alignItems="center">
										<Grid size={{ xs: configuration === TankConfiguration.PortStarboardTanks ? 12 : 2 }}>
											<FormGroup
												label={`${configuration === TankConfiguration.PortStarboardTanks ?
													(intl.formatMessage({ id: `TANK_${tank.position}_PREFIX` })) : ""}
										${intl.formatMessage({ id: "TANK" })} ${tank.positionNumber}`}
												labelPlacement="left-center"
												labelWidth={200}
											/>
										</Grid>
										<Grid size={{ xs: configuration === TankConfiguration.PortStarboardTanks ? 10 : 5 }}>
											<FormGroup
												label={intl.formatMessage({ id: "SHIPS_CAPACITY_M3" })}
												overflow="hidden"
												labelPlacement="left-center"
											>
												{tank.capacityM3 ? DataFormat.volumeM3(tank.capacityM3) : "-"}
											</FormGroup>
										</Grid>

										{showCargos && (
											<Grid size={{ xs: configuration === TankConfiguration.PortStarboardTanks ? 10 : 5 }}>
												{cargosInTank.length > 0 && cargosInTank.map(({ cargo }, loopIndex ) => (
													<FormGroup
														key={cargo.id ?? loopIndex}
														label={intl.formatMessage({ id: "PRODUCT" })}
														overflow="hidden"
														labelPlacement="left-center"
													>
														<Box>
															<ProductLink product={cargo.product} />
														</Box>
													</FormGroup>
												))}
											</Grid>
										)}
									</Grid>
								</FormArrayRow>
							</Grid>
						);
					})}
				</Grid>
			</FormArrayContainer>
		</Grid>
	);
};

export default ShipTanksDetails;
